// Events Admin API - Direct Supabase queries for admin dashboard
import { supabase } from './supabaseClient';

// Get all events with story and attendee counts
export const getEventsOverview = async () => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select(`
        id,
        name,
        event_start,
        event_end,
        eventbrite_id,
        status,
        created_at,
        updated_at,
        collection_id,
        gallery:galleries!gallery_id (
          name,
          address,
          description
        ),
        stories:artspeaks_stories!event_id (
          story_id,
          title,
          artist_uuid,
          featured,
          performance_order
        )
      `)
      .order('event_start', { ascending: false });

    if (error) throw error;

    // Process the data to add counts and fetch attendee data separately
    const processedData = await Promise.all(data?.map(async (event) => {
      // Fetch attendee data using the RPC function for each event
      let attendeeCount = 0;
      let checkedInCount = 0;
      
      if (event.eventbrite_id) {
        try {
          const attendeeResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/event_attendees`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
            },
            body: JSON.stringify({
              eventbrite_id: parseInt(event.eventbrite_id),
              limit_val: 1,
              offset_val: 0,
              search_query: ''
            })
          });

          if (attendeeResponse.ok) {
            const attendeeData = await attendeeResponse.json();
            attendeeCount = attendeeData.total_tickets || 0;
            checkedInCount = attendeeData.total_checked_in || 0;
          }
        } catch (attendeeError) {
          console.error(`Error fetching attendees for event ${event.id}:`, attendeeError);
        }
      }

      return {
        ...event,
        story_count: event.stories?.length || 0,
        attendee_count: attendeeCount,
        checked_in_count: checkedInCount,
        featured_stories: event.stories?.filter(s => s.featured)?.length || 0
      };
    }) || []);

    // Sort events: events with stories and collections first, then by date
    const sortedData = processedData.sort((a, b) => {
      // Check if event has stories
      const aHasStories = a.story_count > 0;
      const bHasStories = b.story_count > 0;
      
      // Check if event has collection (assuming collection_id exists)
      const aHasCollection = a.collection_id != null;
      const bHasCollection = b.collection_id != null;
      
      // Debug logging
      console.log(`Comparing: ${a.name} (stories: ${a.story_count}, collection: ${aHasCollection}, date: ${a.event_start}) vs ${b.name} (stories: ${b.story_count}, collection: ${bHasCollection}, date: ${b.event_start})`);
      
      // Priority: events with both stories and collections
      if (aHasStories && aHasCollection && !(bHasStories && bHasCollection)) {
        console.log(`${a.name} wins - has both stories and collection`);
        return -1;
      }
      if (bHasStories && bHasCollection && !(aHasStories && aHasCollection)) {
        console.log(`${b.name} wins - has both stories and collection`);
        return 1;
      }
      
      // Secondary: events with stories only
      if (aHasStories && !bHasStories) {
        console.log(`${a.name} wins - has stories only`);
        return -1;
      }
      if (bHasStories && !aHasStories) {
        console.log(`${b.name} wins - has stories only`);
        return 1;
      }
      
      // Tertiary: events with collections only
      if (aHasCollection && !bHasCollection) {
        console.log(`${a.name} wins - has collection only`);
        return -1;
      }
      if (bHasCollection && !aHasCollection) {
        console.log(`${b.name} wins - has collection only`);
        return 1;
      }
      
      // Finally: sort by date (newest first)
      const dateComparison = new Date(b.event_start) - new Date(a.event_start);
      console.log(`Date comparison: ${b.name} (${b.event_start}) vs ${a.name} (${a.event_start}) = ${dateComparison}`);
      return dateComparison;
    });

    return sortedData;
  } catch (error) {
    console.error('Error fetching events overview:', error);
    throw error;
  }
};

// Get event analytics
export const getEventAnalytics = async (eventId = null) => {
  try {
    let query = supabase
      .from('artspeaks_stories')
      .select(`
        story_id,
        title,
        story_type,
        featured,
        created_at,
        published_at,
        event_id,
        artist:users!artist_uuid (
          nickname,
          handle
        ),
        artwork:vcs!vc_id (
          title
        )
      `);

    if (eventId) {
      query = query.eq('event_id', eventId);
    }

    const { data: stories, error: storiesError } = await query;

    if (storiesError) throw storiesError;

    // Get engagement data
    const { data: engagement, error: engagementError } = await supabase
      .from('user_activity')
      .select('activity_type, timestamp, story_id')
      .in('activity_type', ['viewed_story', 'liked_story', 'shared_story']);

    if (engagementError) throw engagementError;

    // Calculate analytics
    const analytics = {
      total_stories: stories?.length || 0,
      featured_stories: stories?.filter(s => s.featured)?.length || 0,
      story_types: stories?.reduce((acc, story) => {
        acc[story.story_type] = (acc[story.story_type] || 0) + 1;
        return acc;
      }, {}) || {},
      total_views: engagement?.filter(e => e.activity_type === 'viewed_story')?.length || 0,
      total_likes: engagement?.filter(e => e.activity_type === 'liked_story')?.length || 0,
      total_shares: engagement?.filter(e => e.activity_type === 'shared_story')?.length || 0,
      recent_stories: stories?.slice(0, 5) || []
    };

    return analytics;
  } catch (error) {
    console.error('Error fetching event analytics:', error);
    throw error;
  }
};

// Create quick story
export const createQuickStory = async (storyData) => {
  try {
    const { data, error } = await supabase
      .from('artspeaks_stories')
      .insert({
        vc_id: storyData.vc_id,
        artist_uuid: storyData.artist_uuid,
        title: storyData.title,
        story_text: storyData.story_text,
        inspiration_text: storyData.inspiration_text,
        process_description: storyData.process_description,
        story_type: storyData.story_type || 'inspiration',
        visibility: 'public',
        featured: storyData.featured || false,
        event_id: storyData.event_id,
        performed_at_event: storyData.performed_at_event || false,
        performance_order: storyData.performance_order,
        cover_image: storyData.cover_image,
        audio_url: storyData.audio_url,
        video_url: storyData.video_url,
        published_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select('story_id')
      .single();

    if (error) throw error;

    // Log activity
    await supabase
      .from('user_activity')
      .insert({
        uuid: storyData.artist_uuid,
        story_id: data.story_id,
        vc_id: storyData.vc_id,
        activity_type: 'created_story',
        timestamp: new Date().toISOString(),
        event_id: storyData.event_id
      });

    return data;
  } catch (error) {
    console.error('Error creating quick story:', error);
    throw error;
  }
};

// Get users for story creation
export const getUsersForStoryCreation = async () => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('uuid, nickname, handle, first_name, last_name, profile_img')
      .order('nickname', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching users for story creation:', error);
    throw error;
  }
};

// Get artworks for story creation
export const getArtworksForStoryCreation = async () => {
  try {
    const { data, error } = await supabase
      .from('vcs')
      .select('vc_id, title, cover_image, creator:users!uuid (nickname, handle)')
      .order('title', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching artworks for story creation:', error);
    throw error;
  }
};

// Update story featured status
export const updateStoryFeatured = async (storyId, featured) => {
  try {
    const { data, error } = await supabase
      .from('artspeaks_stories')
      .update({ 
        featured,
        updated_at: new Date().toISOString()
      })
      .eq('story_id', storyId)
      .select('story_id, featured')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating story featured status:', error);
    throw error;
  }
};

// Delete story
export const deleteStory = async (storyId) => {
  try {
    const { error } = await supabase
      .from('artspeaks_stories')
      .delete()
      .eq('story_id', storyId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting story:', error);
    throw error;
  }
};

// Get event detail
export const getEventDetail = async (eventId) => {
  try {
    console.log('Fetching event detail for eventId:', eventId, 'type:', typeof eventId);
    
    // Convert eventId to integer if it's a string
    const numericEventId = parseInt(eventId);
    if (isNaN(numericEventId)) {
      throw new Error(`Invalid event ID: ${eventId}`);
    }
    
    const { data, error } = await supabase
      .from('events')
      .select(`
        id, 
        name, 
        description, 
        event_start, 
        event_end, 
        status, 
        featured,
        eventbrite_id,
        collection_id,
        cover_image,
        ticket_sales_enabled,
        entertainment_tax_enabled,
        entertainment_tax_rate,
        gallery:galleries!gallery_id (
          name, 
          address, 
          description
        ),
        stories:artspeaks_stories!event_id (
          story_id,
          title,
          featured,
          created_at
        )
      `)
      .eq('id', numericEventId)
      .single();

    console.log('Event detail query result:', { data, error });

    if (error) throw error;

    if (!data) {
      throw new Error(`Event with ID ${numericEventId} not found`);
    }

    // Transform the data to flatten gallery info
    const transformedData = {
      ...data,
      gallery_name: data.gallery?.name,
      gallery_address: data.gallery?.address,
      gallery_description: data.gallery?.description,
      stories_count: data.stories?.length || 0
    };

    console.log('Transformed event data:', transformedData);
    return { data: transformedData, error: null };
  } catch (error) {
    console.error('Error fetching event detail:', error);
    return { data: null, error };
  }
};

// Get event collection artworks
export const getEventCollection = async (eventId, limit = null, offset = 0) => {
  try {
    // First get the event to find its collection_id
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('collection_id')
      .eq('id', eventId)
      .single();

    if (eventError) throw eventError;

    if (!event.collection_id) {
      return [];
    }

    // Build the query
    let query = supabase
      .from('collection_vc_mapping')
      .select(`
        vc_id,
        accepted,
        order_num,
        created_at,
        vcs (
          vc_id,
          title,
          description,
          cover_image,
          access_control,
          created_at,
          updated_at,
          width,
          height,
          depth,
          price,
          status,
          more,
          uuid,
          location,
          users (
            uuid,
            first_name,
            last_name,
            nickname,
            handle,
            profile_img,
            email,
            bio
          )
        )
      `)
      .eq('collection_id', event.collection_id)
      .order('order_num', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    // Only apply range if limit is specified
    if (limit !== null) {
      query = query.range(offset, offset + limit - 1);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Process artwork data to match the expected format
    const processedData = (data || []).map(item => ({
      ...item.vcs,
      artist: item.vcs.users,
      // Add mapping data
      accepted: item.accepted,
      order_num: item.order_num,
      submission_date: item.created_at
    }));

    return { data: processedData, error: null };
  } catch (error) {
    console.error('Error fetching event collection:', error);
    return { data: null, error };
  }
};

// Get event stories
export const getEventStories = async (eventId) => {
  try {
    console.log('🔍 getEventStories called with eventId:', eventId, 'type:', typeof eventId);
    
    const { data, error } = await supabase
      .from('artspeaks_stories')
      .select(`
        story_id,
        title,
        story_text,
        inspiration_text,
        process_description,
        story_type,
        featured,
        created_at,
        updated_at,
        artist:users!artist_uuid (
          uuid,
          nickname,
          first_name,
          last_name
        ),
        artwork:vcs!vc_id (
          vc_id,
          title,
          cover_image
        )
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    console.log('🔍 Supabase query result:', { data: data?.length || 0, error });
    if (error) throw error;

    // Transform the data to include artist name and combine story content
    const transformedData = (data || []).map(story => ({
      ...story,
      content: story.story_text || story.inspiration_text || story.process_description || '',
      artist_name: story.artist ? 
        (story.artist.nickname || `${story.artist.first_name} ${story.artist.last_name}`) :
        'Unknown Artist'
    }));

    return { data: transformedData, error: null };
  } catch (error) {
    console.error('Error fetching event stories:', error);
    return { data: null, error };
  }
};

// Get event attendees
export const getEventAttendees = async (eventId) => {
  try {
    // First get the event to find its eventbrite_id
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('eventbrite_id')
      .eq('id', eventId)
      .single();

    if (eventError) throw eventError;

    if (!event.eventbrite_id) {
      return [];
    }

    // Use the RPC function to get attendees (same as CollectionDetailAdmin)
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/event_attendees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({
        eventbrite_id: parseInt(event.eventbrite_id),
        limit_val: 1000,
        offset_val: 0,
        search_query: ''
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch attendees');
    }

    const result = await response.json();
    return { data: result.attendees || [], error: null };
  } catch (error) {
    console.error('Error fetching event attendees:', error);
    return { data: null, error };
  }
};

// Create artwork story
export const createArtworkStory = async (storyData) => {
  try {
    const { data, error } = await supabase
      .from('artspeaks_stories')
      .insert({
        title: storyData.title,
        story_text: storyData.content,
        story_type: storyData.story_type || 'artwork',
        vc_id: storyData.vc_id,
        event_id: storyData.event_id,
        performed_at_event: storyData.performed_at_event || true,
        artist_uuid: storyData.artist_uuid,
        featured: storyData.featured || false,
        visibility: 'public',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select('story_id')
      .single();

    if (error) throw error;

    // Log activity
    await supabase
      .from('user_activity')
      .insert({
        uuid: storyData.artist_uuid,
        story_id: data.story_id,
        vc_id: storyData.vc_id,
        activity_type: 'created_story',
        timestamp: new Date().toISOString(),
        event_id: storyData.event_id
      });

    return data;
  } catch (error) {
    console.error('Error creating artwork story:', error);
    throw error;
  }
};

// Get all galleries for event creation
export const getGalleries = async () => {
  try {
    const { data, error } = await supabase
      .from('galleries')
      .select(`
        gallery_id,
        name,
        address,
        description
      `)
      .eq('approved', true)
      .order('name');

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching galleries:', error);
    throw error;
  }
};

// Convert local datetime-local string to UTC ISO string
const localDatetimeToUTC = (localDatetimeStr) => {
  if (!localDatetimeStr) return null;
  try {
    const localDate = new Date(localDatetimeStr);
    if (isNaN(localDate.getTime())) return localDatetimeStr;
    return localDate.toISOString();
  } catch (e) {
    return localDatetimeStr;
  }
};

// Create a new event
export const createEvent = async (eventData) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .insert({
        name: eventData.name,
        description: eventData.description || null,
        event_start: localDatetimeToUTC(eventData.event_start),
        event_end: localDatetimeToUTC(eventData.event_end) || null,
        gallery_id: eventData.gallery_id,
        event_type: eventData.event_type || 'show',
        cover_image: eventData.cover_image || null,
        status: 'active'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

// ==========================================
// TICKET MANAGEMENT API FUNCTIONS
// ==========================================

// Get all ticket types for an event
export const getEventTicketTypes = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('event_ticket_types')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching ticket types:', error);
    throw error;
  }
};

// Create a new ticket type
export const createTicketType = async (ticketTypeData) => {
  try {
    const { data, error } = await supabase
      .from('event_ticket_types')
      .insert({
        event_id: ticketTypeData.event_id,
        ticket_type_name: ticketTypeData.ticket_type_name,
        description: ticketTypeData.description || null,
        regular_price: ticketTypeData.regular_price,
        early_bird_price: ticketTypeData.early_bird_price || null,
        early_bird_deadline: ticketTypeData.early_bird_deadline || null,
        tickets_available: ticketTypeData.tickets_available,
        tickets_sold: 0,
        sale_start: ticketTypeData.sale_start || null,
        sale_end: ticketTypeData.sale_end || null,
        is_hidden: ticketTypeData.is_hidden || false
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating ticket type:', error);
    throw error;
  }
};

// Update a ticket type
export const updateTicketType = async (ticketTypeId, ticketTypeData) => {
  try {
    const { data, error } = await supabase
      .from('event_ticket_types')
      .update({
        ticket_type_name: ticketTypeData.ticket_type_name,
        description: ticketTypeData.description || null,
        regular_price: ticketTypeData.regular_price,
        early_bird_price: ticketTypeData.early_bird_price || null,
        early_bird_deadline: ticketTypeData.early_bird_deadline || null,
        tickets_available: ticketTypeData.tickets_available,
        sale_start: ticketTypeData.sale_start || null,
        sale_end: ticketTypeData.sale_end || null,
        is_hidden: ticketTypeData.is_hidden || false
      })
      .eq('id', ticketTypeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating ticket type:', error);
    throw error;
  }
};

// Delete a ticket type
export const deleteTicketType = async (ticketTypeId) => {
  try {
    const { error } = await supabase
      .from('event_ticket_types')
      .delete()
      .eq('id', ticketTypeId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting ticket type:', error);
    throw error;
  }
};

// Get all ticket sales for an event (supports both Eventbrite and native Stripe)
export const getEventTicketSales = async (eventId) => {
  try {
    // First, check if this event has an eventbrite_id
    const { data: eventData, error: eventError } = await supabase
      .from('events')
      .select('id, eventbrite_id')
      .eq('id', eventId)
      .single();

    if (eventError) throw eventError;

    let allTickets = [];

    // Always fetch native Stripe tickets from user_event_tickets
    const { data: stripeData, error: stripeError } = await supabase
      .from('user_event_tickets')
      .select(`
        id,
        user_uuid,
        event_id,
        ticket_type_id,
        stripe_session_id,
        quantity,
        amount_paid,
        payment_status,
        ticket_code,
        checked_in,
        check_in_time,
        created_at,
        ticket_type:event_ticket_types!ticket_type_id (
          ticket_type_name
        ),
        user:users!user_uuid (
          email,
          first_name,
          last_name,
          profile_img
        )
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (stripeError) {
      console.error('Error fetching Stripe tickets:', stripeError);
    } else if (stripeData?.length > 0) {
      // Group tickets by stripe_session_id to show orders (not individual tickets)
      const ordersMap = new Map();
      
      stripeData.forEach(sale => {
        const sessionId = sale.stripe_session_id || sale.id; // Fallback to id if no session
        
        if (ordersMap.has(sessionId)) {
          // Add to existing order
          const existing = ordersMap.get(sessionId);
          existing.quantity += 1;
          existing.amount_paid += parseFloat(sale.amount_paid) || 0;
        } else {
          // Create new order entry
          ordersMap.set(sessionId, {
            ...sale,
            stripe_session_id: sessionId,
            quantity: 1,
            amount_paid: parseFloat(sale.amount_paid) || 0,
            ticket_type_name: sale.ticket_type?.ticket_type_name || 'General',
            customer_name: sale.user 
              ? `${sale.user.first_name || ''} ${sale.user.last_name || ''}`.trim() || sale.user.email
              : 'Unknown',
            customer_email: sale.user?.email || '',
            customer_avatar: sale.user?.profile_img || null,
            source: 'stripe'
          });
        }
      });
      
      allTickets = Array.from(ordersMap.values());
    }

    // If event has Eventbrite ID, also fetch from rsvp_attendees
    if (eventData?.eventbrite_id) {
      const { data: attendees, error: attendeesError } = await supabase
        .from('rsvp_attendees')
        .select('*')
        .eq('event_id', eventData.eventbrite_id)
        .order('order_date', { ascending: false });

      if (attendeesError) {
        console.error('Error fetching Eventbrite attendees:', attendeesError);
      } else if (attendees?.length > 0) {
        // Map Eventbrite attendees to common format
        const eventbriteTickets = attendees.map(attendee => ({
          id: attendee.id,
          user_uuid: attendee.user_uuid,
          event_id: eventId,
          ticket_type_id: null,
          quantity: 1,
          amount_paid: attendee.ticket_price || 0,
          payment_status: 'completed',
          ticket_code: attendee.ticket_id,
          checked_in: attendee.checked_in || false,
          check_in_time: null,
          created_at: attendee.order_date,
          ticket_type_name: attendee.ticket_tier || 'General Admission',
          customer_name: `${attendee.attendee_first_name || ''} ${attendee.attendee_last_name || ''}`.trim() || 'Unknown',
          customer_email: attendee.attendee_email || '',
          customer_avatar: null,
          source: 'eventbrite',
          gross_sales: attendee.gross_sales,
          order_id: attendee.order_id
        }));

        allTickets = [...allTickets, ...eventbriteTickets];
      }
    }

    // Sort all tickets by date (newest first)
    allTickets.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return allTickets;
  } catch (error) {
    console.error('Error fetching ticket sales:', error);
    throw error;
  }
};

// Check in a ticket
export const checkInTicket = async (ticketId, checkedIn = true) => {
  try {
    const { data, error } = await supabase
      .from('user_event_tickets')
      .update({
        checked_in: checkedIn,
        check_in_time: checkedIn ? new Date().toISOString() : null
      })
      .eq('id', ticketId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error checking in ticket:', error);
    throw error;
  }
};

// Get ticket analytics for an event
export const getEventTicketAnalytics = async (eventId) => {
  try {
    // Get all ticket sales for the event
    const { data: sales, error: salesError } = await supabase
      .from('user_event_tickets')
      .select('amount_paid, quantity, payment_status, checked_in')
      .eq('event_id', eventId)
      .eq('payment_status', 'completed');

    if (salesError) throw salesError;

    // Get all ticket types for capacity info
    const { data: ticketTypes, error: typesError } = await supabase
      .from('event_ticket_types')
      .select('tickets_available, tickets_sold')
      .eq('event_id', eventId);

    if (typesError) throw typesError;

    // Calculate analytics
    const totalRevenue = sales?.reduce((sum, sale) => sum + (sale.amount_paid || 0), 0) || 0;
    const totalTicketsSold = sales?.reduce((sum, sale) => sum + (sale.quantity || 0), 0) || 0;
    const totalCheckedIn = sales?.filter(s => s.checked_in).length || 0;
    const totalCapacity = ticketTypes?.reduce((sum, t) => sum + (t.tickets_available || 0), 0) || 0;

    return {
      totalRevenue,
      totalTicketsSold,
      totalCheckedIn,
      totalCapacity,
      ticketTypesCount: ticketTypes?.length || 0,
      salesByDay: [], // Could add time-series data here
      conversionRate: totalCapacity > 0 ? (totalTicketsSold / totalCapacity) * 100 : 0
    };
  } catch (error) {
    console.error('Error fetching ticket analytics:', error);
    throw error;
  }
};
